import { useEffect, useMemo, useState } from 'react';
import { Tab, Tabs } from '@nextui-org/react';
import ModalSlideFilter from 'common/src/components/filters/modal-sidebar-filter';
import useDataStore from 'common/src/stores/useDataStore';
import useFilterStore from 'common/src/stores/useFiltersStore';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { getSeasonWeeksForSpecies } from 'common/src/utils/seasonWeeks';
import { getIndustryVolumePulse, type IndustryVolumeError } from 'common/src/api/industryVolume';
import Header from '../../components/layouts/Header';
import { Alert, EmptyState, FiltersToggle, LoadingState, PageLayout } from '../../components/ui';
import PulseSummary, { type PulseBasis } from '../../components/industry-volume/PulseSummary';
import ArrivalsCard from '../../components/industry-volume/ArrivalsCard';
import PulsePreview from '../../components/industry-volume/PulsePreview';
import VolumeCurveCard from '../../components/industry-volume/VolumeCurveCard';
import {
  ExporterRankingCard,
  MarketsCard,
  VarietyMixCard,
} from '../../components/industry-volume/PulseTables';
import type { PulseData } from '../../utils/industry-volume/build-pulse';
import { day, week } from '../../components/industry-volume/format';
import { VolumeUnitContext, type VolumeUnit } from '../../components/industry-volume/unit';

/**
 * Volumen Exportación: cuánto lleva despachado la industria a la fecha, contra
 * la temporada anterior en la misma semana, y dónde está la exportadora del
 * usuario. Sale de la base de volumen que sube un superadmin.
 */
export default function IndustryVolumePage() {
  const { t } = useTranslation('volume');
  const { selectedSpecies } = useDataStore();
  const { selectedSeason } = useFilterStore();
  const speciesId = selectedSpecies?.id;
  const weeks = useMemo(
    () => getSeasonWeeksForSpecies(speciesId, selectedSeason),
    [speciesId, selectedSeason]
  );
  const [basis, setBasis] = useState<PulseBasis>('etd');
  const [unit, setUnit] = useState<VolumeUnit>('kg');
  const [filtersOpen, setFiltersOpen] = useState(true);
  // Kilos por caja equivalente de la especie (en cereza, 5).
  const kgPerBox = Number(selectedSpecies?.default_unit_price) || 5;
  const [data, setData] = useState<PulseData | null>(null);
  const [error, setError] = useState<IndustryVolumeError | null>(null);

  useEffect(() => {
    if (!speciesId || !selectedSeason) return;
    let active = true;
    setError(null);
    getIndustryVolumePulse(speciesId, selectedSeason, weeks, basis)
      .then((result) => active && setData(result as PulseData))
      .catch((e: IndustryVolumeError) => active && setError(e));
    return () => {
      active = false;
    };
  }, [speciesId, selectedSeason, weeks, basis]);

  // Al cambiar de especie o temporada se vuelve a la carga inicial.
  useEffect(() => {
    setData(null);
  }, [speciesId, selectedSeason]);

  const content = () => {
    if (error?.code === '42501') {
      return (
        <EmptyState
          icon='tabler:lock'
          title={t('Volumen no está disponible para tu exportadora.')}
        />
      );
    }
    if (error) {
      return (
        <Alert variant='danger' showIcon>
          {t('No se pudo cargar el volumen: {{error}}', { error: error.message })}
        </Alert>
      );
    }
    if (!data) return <LoadingState label={t('Cargando…')} />;
    // Cambió la base (zarpe o arribo): se queda lo anterior, atenuado, hasta que llega.
    const loading = data.basis !== basis;
    if (!data.has_data) {
      return (
        <EmptyState
          icon='tabler:chart-bar'
          title={t('La base de volumen todavía no tiene datos de {{species}} {{season}}.', {
            species: selectedSpecies?.name ?? '',
            season: selectedSeason,
          })}
          description={t('Elige otra temporada en el menú lateral.')}
        />
      );
    }
    if (data.access === 'preview') {
      return (
        <div className='flex flex-col gap-6'>
          <PulsePreview data={data} />
          <p className='text-xs text-default-500'>
            <span className='font-semibold'>{t('Fuente')}: </span>
            {t('Base de exportaciones de Frutas de Chile (ASOEX), kilos netos, todas las vías y destinos.')}
          </p>
        </div>
      );
    }
    return (
      <div className='flex flex-col gap-6' aria-busy={loading}>
        <PulseSummary data={data} loading={loading} />
        <div className={`flex flex-col gap-6 transition-opacity ${loading ? 'opacity-50' : ''}`}>
          <VolumeCurveCard key={`curve-${data.season}`} data={data} weeks={weeks} speciesId={speciesId!} />
          <VarietyMixCard data={data} />
          <ExporterRankingCard data={data} />
          <MarketsCard data={data} />
        </div>
        <ArrivalsCard
          key={`arrivals-${data.season}`}
          speciesId={speciesId!}
          season={data.season}
          basis={basis}
        />
        <div className='grid grid-cols-1 gap-4 text-xs text-default-500 md:grid-cols-2 xl:grid-cols-4'>
          <div>
            <span className='font-semibold'>{t('Fuente')}: </span>
            {t('Base de exportaciones de Frutas de Chile (ASOEX), kilos netos, todas las vías y destinos.')}
          </div>
          <div>
            <span className='font-semibold'>{t('Semanas')}: </span>
            {t('Semana ISO de la fecha de zarpe. Las semanas antes del inicio de la temporada se cuentan en la primera y las posteriores al cierre, en la última.')}
          </div>
          <div>
            <span className='font-semibold'>{t('Arribos')}: </span>
            {t('Arribo real del tracking de la nave cuando existe (misma nave, zarpe ±4 días); si no, el arribo estimado de la base o el tránsito mediano de la ruta.')}
          </div>
          <div>
            <span className='font-semibold'>{t('Confidencialidad')}: </span>
            {t('Solo datos de embarque. No cruza liquidaciones ni precios de ninguna exportadora.')}
          </div>
        </div>
      </div>
    );
  };

  return (
    <VolumeUnitContext.Provider value={{ unit, kgPerBox }}>
      <div className='flex h-screen w-full overflow-x-hidden'>
      {/* Unidad y base de las semanas: cambian toda la pantalla. */}
      <ModalSlideFilter isOpen={filtersOpen} onClose={() => setFiltersOpen(false)}>
        <div className='h-full overflow-y-auto bg-background p-4 pt-8'>
          <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-1'>
              <p className='text-sm text-default-500'>{t('Unidad')}</p>
              <Tabs
                fullWidth
                size='sm'
                aria-label={t('Unidad')}
                selectedKey={unit}
                onSelectionChange={(key) => setUnit(key as VolumeUnit)}
              >
                <Tab key='kg' title={t('Toneladas')} />
                <Tab key='box' title={t('Miles de cajas')} />
              </Tabs>
            </div>
            <div className='flex flex-col gap-1'>
              <p className='text-sm text-default-500'>{t('Semana de referencia')}</p>
              <Tabs
                fullWidth
                size='sm'
                aria-label={t('Semana de referencia')}
                selectedKey={basis}
                onSelectionChange={(key) => setBasis(key as PulseBasis)}
              >
                <Tab key='etd' title={t('Zarpe (ETD)')} />
                <Tab key='eta' title={t('Arribo (ETA)')} />
              </Tabs>
            </div>
          </div>
        </div>
      </ModalSlideFilter>
      <PageLayout>
        <Header
          title={t('Volumen de Exportación · Industria')}
          description={
            data?.has_data
              ? t('Datos al {{date}} (último zarpe informado, semana {{week}}). Fuente: Frutas de Chile / ASOEX.', {
                  date: day(data.last_etd),
                  week: week(data.cutoff_week),
                })
              : t('Fuente: Frutas de Chile / ASOEX.')
          }
          actions={
            <div className='flex items-center gap-2'>
              <span className='text-xs uppercase tracking-wide text-default-500'>{t('Fuente')}</span>
              <img
                src='/logos/frutas-de-chile.jpg'
                alt='Frutas de Chile (ASOEX)'
                className='h-10 w-auto rounded-small'
              />
            </div>
          }
        />
        <div className='mb-4'>
          <FiltersToggle
            isOpen={filtersOpen}
            onToggle={() => setFiltersOpen(!filtersOpen)}
            label={filtersOpen ? t('Ocultar filtros') : t('Mostrar filtros')}
          />
        </div>
        {content()}
      </PageLayout>
      </div>
    </VolumeUnitContext.Provider>
  );
}
